import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { timesheetService, userService } from '../../services/api';
import { FiChevronLeft, FiChevronRight, FiPlus, FiDownload, FiUpload, FiX, FiCheckCircle, FiAlertCircle, FiAlertTriangle } from 'react-icons/fi';
import { format, startOfWeek, endOfWeek, addDays, isToday } from 'date-fns';
import { exportToExcel } from '../../utils/exportExcel';
import * as XLSX from 'xlsx';
import './TimesheetList.css';
import '../Admin/Admin.css';

export const TimesheetList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isReadOnly = user?.isActive === false;
  const isAdmin = user?.role === 'admin';
  const [currentDate, setCurrentDate] = useState(new Date());
  const [timesheets, setTimesheets] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadUserId, setUploadUserId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(user?.id || '');

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'manager') {
      fetchEmployees();
    }
  }, [user?.role]);

  useEffect(() => {
    fetchTimesheets();
  }, [currentDate, selectedEmployee]);

  const fetchEmployees = async () => {
    try {
      const response = await userService.getAll({ limit: 1000 });
      setEmployees(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const fetchTimesheets = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await timesheetService.getAll({
        startDate: format(weekStart, 'yyyy-MM-dd'),
        endDate: format(weekEnd, 'yyyy-MM-dd'),
        userId: selectedEmployee,
      });
      setTimesheets(response.data.timesheets || []);
    } catch (err) {
      setError('Failed to load timesheets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInWeek = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(weekStart, i));
    }
    return days;
  };

  const getTimesheetForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return timesheets.find((ts) => ts.date === dateStr);
  };

  const calculateTotalHours = () => {
    return timesheets.reduce((sum, ts) => sum + (ts.totalHours || 0), 0);
  };

  const handlePrevWeek = () => {
    setCurrentDate((prev) => addDays(prev, -7));
  };

  const handleNextWeek = () => {
    setCurrentDate((prev) => addDays(prev, 7));
  };

  const handleDayClick = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    navigate(`/timesheet/${dateStr}`);
  };

  const handleAddEntry = () => {
    const dateStr = format(new Date(), 'yyyy-MM-dd');
    navigate(`/timesheet/${dateStr}`);
  };

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    // Get current month and generate a sample sheet
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0-indexed
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    const rows = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const dayName = dayNames[date.getDay()];
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      rows.push({
        'S.No': d,
        'Date': date,
        'Task': isWeekend ? dayName : '',
        'Resource': isWeekend ? '' : '',
        'Hours Worked': isWeekend ? '' : '',
        'Remarks': '',
        'Status': ''
      });
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    // Format date column as dates
    for (let r = 1; r <= daysInMonth; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c: 1 })];
      if (cell) { cell.t = 'd'; cell.z = 'DD-MM-YYYY'; }
    }
    ws['!cols'] = [
      { wch: 6 },  // S.No
      { wch: 14 }, // Date
      { wch: 50 }, // Task
      { wch: 16 }, // Resource
      { wch: 14 }, // Hours Worked
      { wch: 20 }, // Remarks
      { wch: 14 }, // Status
    ];
    const sheetName = `${monthNames[month]}-${String(year).slice(2)}`;
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `Timesheet_Template_${monthNames[month]}_${year}.xlsx`);
  };

  const handleBulkUpload = async () => {
    if (!uploadFile || !uploadUserId) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('userId', uploadUserId);
      const res = await timesheetService.bulkUpload(formData);
      setUploadResult(res.data);
      if (res.data.created > 0) fetchTimesheets();
    } catch (err) {
      setUploadResult({
        message: err.response?.data?.error || 'Upload failed',
        created: 0, skipped: 0, errorCount: 1, sheets: []
      });
    } finally {
      setUploading(false);
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadUserId('');
    setUploadResult(null);
  };

  const days = getDaysInWeek();

  return (
    <div className="timesheet-list-container">
      <div className="timesheet-header">
        <h1>Timesheet</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {timesheets.length > 0 && (
            <button className="btn btn-secondary" onClick={() => {
              const data = timesheets.map(ts => ({
                Date: ts.date,
                Employee: ts.User ? `${ts.User.firstName} ${ts.User.lastName}` : '',
                'Total Hours': ts.totalHours,
                Status: ts.status,
              }));
              exportToExcel(data, `Timesheets_${format(weekStart, 'yyyy-MM-dd')}`);
            }}>
              <FiDownload /> Export
            </button>
          )}
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
              <FiUpload /> Upload Timesheet
            </button>
          )}
          {!isReadOnly && (
            <button className="btn btn-primary" onClick={handleAddEntry}>
              <FiPlus /> Add Entry
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Timesheet Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={closeUploadModal}>
          <div className="modal-content" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Timesheet</h2>
              <button className="modal-close-btn" onClick={closeUploadModal}><FiX size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="upload-step">
                <h3>Step 1: Download Template</h3>
                <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0 0 0.75rem 0' }}>
                  Download the template, share it with the employee to fill in their daily tasks, then upload the completed file.
                </p>
                <button className="btn btn-secondary" onClick={handleDownloadTemplate}>
                  <FiDownload /> Download Template
                </button>
              </div>

              <div className="upload-step">
                <h3>Step 2: Select Employee</h3>
                <select
                  className="filter-select"
                  value={uploadUserId}
                  onChange={(e) => setUploadUserId(e.target.value)}
                  style={{ width: '100%', padding: '0.6rem', marginBottom: '0.5rem' }}
                >
                  <option value="">-- Select Employee --</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName} ({emp.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="upload-step">
                <h3>Step 3: Upload Completed File</h3>
                <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0 0 0.75rem 0' }}>
                  Excel format: Each sheet = one month. Columns: S.No, Date, Task, Resource, Hours Worked, Remarks, Status.
                  Weekends and holidays are skipped automatically.
                </p>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setUploadFile(e.target.files[0] || null)}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleBulkUpload}
                  disabled={!uploadFile || !uploadUserId || uploading}
                  style={{ marginTop: '0.75rem' }}
                >
                  {uploading ? 'Uploading...' : <><FiUpload /> Upload & Process</>}
                </button>
              </div>

              {uploadResult && (
                <div className="upload-results" style={{ background: '#f9fafb', borderRadius: '8px', padding: '1rem', marginTop: '1rem' }}>
                  <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem' }}>Results</h3>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                    {uploadResult.created > 0 && (
                      <span style={{ background: '#dcfce7', color: '#166534', padding: '0.3rem 0.7rem', borderRadius: '20px', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                        <FiCheckCircle /> {uploadResult.created} days created
                      </span>
                    )}
                    {uploadResult.skipped > 0 && (
                      <span style={{ background: '#fef3c7', color: '#92400e', padding: '0.3rem 0.7rem', borderRadius: '20px', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                        <FiAlertTriangle /> {uploadResult.skipped} skipped
                      </span>
                    )}
                    {uploadResult.errorCount > 0 && (
                      <span style={{ background: '#fee2e2', color: '#991b1b', padding: '0.3rem 0.7rem', borderRadius: '20px', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                        <FiAlertCircle /> {uploadResult.errorCount} errors
                      </span>
                    )}
                  </div>
                  {uploadResult.sheets && uploadResult.sheets.length > 0 && (
                    <div style={{ fontSize: '0.82rem', color: '#4b5563' }}>
                      {uploadResult.sheets.map((s, i) => (
                        <div key={i}>{s.sheet}: {s.created} days</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {(user?.role === 'admin' || user?.role === 'manager') && (
        <div className="employee-filter">
          <label>Filter by Employee:</label>
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            <option value={user?.id}>My Timesheets</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="week-navigator">
        <button onClick={handlePrevWeek} className="btn btn-secondary">
          <FiChevronLeft /> Previous
        </button>
        <div className="week-label">
          {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
        </div>
        <button onClick={handleNextWeek} className="btn btn-secondary">
          Next <FiChevronRight />
        </button>
      </div>

      <div className="summary-card">
        <div className="summary-item">
          <span className="summary-label">Total Hours This Week:</span>
          <span className="summary-value">{calculateTotalHours().toFixed(1)}</span>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading timesheets...</div>
      ) : (
        <div className="weekly-grid">
          {days.map((date) => {
            const timesheet = getTimesheetForDate(date);
            const isCurrentDay = isToday(date);

            return (
              <div
                key={format(date, 'yyyy-MM-dd')}
                className={`day-card ${isCurrentDay ? 'today' : ''}`}
                onClick={() => handleDayClick(date)}
              >
                <div className="day-header">
                  <div className="day-name">{format(date, 'EEE')}</div>
                  <div className="day-date">{format(date, 'd')}</div>
                </div>
                <div className="day-content">
                  {timesheet ? (
                    <>
                      <div className="hours">
                        {timesheet.totalHours?.toFixed(1) || '0'} hrs
                      </div>
                      <div
                        className={`status-badge status-${timesheet.status || 'draft'}`}
                      >
                        {timesheet.status || 'draft'}
                      </div>
                    </>
                  ) : (
                    <div className="empty">No entry</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TimesheetList;
